import React, { useEffect, useState } from 'react';
import { Button, Group, Paper, Select, Stack, Text } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGrades } from '../../hooks/useReferenceApi';
import { api } from '../../utils/api';
import { showToast } from '../../utils/toast';
import { perfReviewErrorMessage } from '../../pages/performance-review/utils/errorMessage';

export type GradeDecision = 'promote' | 'confirm' | 'no-change' | 'demote';

export interface OutcomeValues {
  decision?: GradeDecision | null;
  gradeAfter?: string | { _id?: string } | null;
  meetingAt?: string | null;
}

interface Props {
  roundId: string;
  userId: string;
  initial?: OutcomeValues;
}

const DECISIONS: GradeDecision[] = ['promote', 'confirm', 'no-change', 'demote'];

// Итог 1-on-1: решение по грейду, целевой грейд и время встречи. Сотрудник
// видит решение после публикации итога, встречу — сразу. Грейд в профиле
// отсюда не меняется.
export const PerformanceReviewOutcomePanel: React.FC<Props> = ({ roundId, userId, initial }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: grades = [] } = useGrades();
  const [decision, setDecision] = useState<GradeDecision | null>(null);
  const [gradeAfter, setGradeAfter] = useState<string | null>(null);
  const [meetingAt, setMeetingAt] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setDecision(initial?.decision ?? null);
    const target = initial?.gradeAfter;
    setGradeAfter((typeof target === 'object' ? target?._id : target) ?? null);
    setMeetingAt(initial?.meetingAt ?? null);
  }, [userId, initial?.decision, initial?.gradeAfter, initial?.meetingAt]);

  const isGradeChange = decision === 'promote' || decision === 'demote';

  const save = async () => {
    setIsSaving(true);
    try {
      await api(`/performance-review/rounds/${roundId}/participants/${userId}/outcome`, {
        method: 'PUT',
        body: {
          decision,
          gradeAfter: isGradeChange ? gradeAfter : null,
          // DateTimePicker отдаёт локальное «YYYY-MM-DD HH:mm:ss».
          meetingAt: meetingAt ? new Date(meetingAt.replace(' ', 'T')).toISOString() : null,
        },
      });
      showToast({ title: t('perfReview.outcome.saved'), message: '', color: 'teal' });
      // Строки участников и «мои саммари» несут итог — обновить и их.
      queryClient.invalidateQueries({
        predicate: (q) => q.queryKey.some((k) => typeof k === 'string' && k.includes(roundId)),
      });
    } catch (e) {
      showToast({ title: t('common.error'), message: perfReviewErrorMessage(t, e), color: 'red' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Paper withBorder p="sm" radius="md">
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          {t('perfReview.outcome.title')}
        </Text>
        <Select
          label={t('perfReview.outcome.decision')}
          placeholder={t('perfReview.outcome.decisionPlaceholder')}
          data={DECISIONS.map((d) => ({ value: d, label: t(`perfReview.outcome.${d}`) }))}
          value={decision}
          onChange={(v) => setDecision(v as GradeDecision | null)}
          clearable
        />
        {isGradeChange && (
          <Select
            label={t('perfReview.outcome.gradeAfter')}
            data={(grades as { _id: string; title: string }[]).map((g) => ({
              value: g._id,
              label: g.title,
            }))}
            value={gradeAfter}
            onChange={setGradeAfter}
            searchable
          />
        )}
        <DateTimePicker
          label={t('perfReview.outcome.meetingAt')}
          value={meetingAt}
          onChange={setMeetingAt}
          valueFormat="DD.MM.YYYY HH:mm"
          placeholder={t('perfReview.outcome.meetingPlaceholder')}
          clearable
        />
        <Group justify="space-between" align="flex-end" wrap="nowrap" gap="sm">
          <Text size="xs" c="dimmed">
            {t('perfReview.outcome.hint')}
          </Text>
          <Button
            variant="light"
            style={{ flexShrink: 0 }}
            onClick={save}
            loading={isSaving}
            disabled={isGradeChange && !gradeAfter}
          >
            {t('perfReview.outcome.save')}
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
};
